<template>
  <b-modal ref="modal" :id="modalId" centered hide-footer title="Project Detail" size="xl" hide-header-close>
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading project detail...</p>
    </div>

    <div v-else-if="data" class="d-flex flex-column gap-2">
      <b-row class="d-flex flex-column p-4 gap-4">
        <div class="project-grid gap-4">
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Project Name</span>
            <span class="project-value">{{ data?.project?.name }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Project Description</span>
            <span class="project-value desc-truncate">{{ data?.project?.description }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Duration</span>
            <span class="project-value">{{ data?.project?.duration }} Days</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Status</span>
            <span :class="getTrainingStatusClass(data.status) + ' mr-auto'">
              {{ snakeToTitle(data.status) }}
            </span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Start Date</span>
            <span class="project-value">{{ formatDate(data?.project?.start_date) }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">End Date</span>
            <span class="project-value">{{ formatDate(data?.project?.end_date) }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Business Need</span>
            <span class="project-value">{{ data?.project?.business_need }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Objectives</span>
            <span class="project-value">{{ data?.project?.objectives }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Goals</span>
            <span class="project-value">{{ data?.project?.goals }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Scope</span>
            <span class="project-value">{{ data?.project?.scope }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Constraints</span>
            <span class="project-value">{{ data?.project?.constraints }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Deliverables</span>
            <span class="project-value">{{ data?.project?.deliverables }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Impact A</span>
            <span class="project-value">{{ data?.project?.impact_a }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Impact</span>
            <span class="project-value">{{ data?.project?.impact }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Impact B</span>
            <span class="project-value">{{ data?.project?.impact_b }}</span>
          </div>
          <div class="project-item d-flex flex-column gap-1">
            <span class="project-label">Created By</span>
            <span class="project-value">{{ data?.project?.created_by }}</span>
          </div>
        </div>
        <div class="d-flex flex-column gap-1 w-75">
          <span class="project-label">Project Teams</span>
          <vue-good-table
            :columns="memberColumns"
            :rows="data?.project?.members"
            style-class="vgt-table border-0 table-auto"
            styleClass="vgt-table condensed"
          >
            <div slot="emptystate" class="text-center font-size-14">No data available</div>

            <template slot="table-row" slot-scope="props">
              <span class="d-flex align-items-center justify-content-start">
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
        <div class="d-flex flex-column gap-1 w-75">
          <span class="project-label">Attachment</span>
          <a v-if="data?.project?.attachments" class="font-weight-bold" :href="data?.project?.attachments" target="_blank"
            >Attachment File</a
          >
        </div>
        <div class="d-flex flex-column gap-1">
          <span class="project-label">Milestones</span>
          <vue-good-table
            :columns="milestoneColumns"
            :rows="data?.project?.activities"
            style-class="vgt-table border-0 table-auto"
            styleClass="vgt-table condensed"
          >
            <div slot="emptystate" class="text-center font-size-14">No data available</div>

            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'action'" class="d-flex gap-3 justify-content-center align-items-center">
                <button
                  v-if="
                    props.row.count_progress_activity <= 100 &&
                    props.row.status !== 'in review' &&
                    props.row.status !== 'completed' &&
                    props.row.is_allow_update
                  "
                  class="btn btn-sm btn-warning"
                  v-b-tooltip.hover.left="'Update Activity'"
                  @click="updateActivity(props.row)"
                >
                  <span class="fe fe-edit"></span>
                </button>
                <button
                  v-else
                  class="btn btn-sm btn-info mx-1 my-1"
                  @click="updateActivity(props.row)"
                  v-b-tooltip.hover.left="'View Activity'"
                >
                  <span class="fe fe-eye"></span>
                </button>
              </span>

              <span v-else-if="props.column.field === 'evidence'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <a v-if="props.row.evidence" :href="props.row.evidence" target="_blank">File</a>
                  <span v-else>No evidence available</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'percentage'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ props.row.count_progress_activity || 0 }}%</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'start_date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.start_date) || "-" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'end_date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.end_date) || "-" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'actual_start_date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.actual_start_date) || "-" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'actual_end_date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.actual_end_date) || "-" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'feedback'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ props.row?.feedback?.feedback || "No feedback available" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'assigned'">
                <div
                  v-if="props.row.employees.length > 0"
                  v-for="(item, index) in props.row.employees"
                  :key="index"
                  class="d-flex flex-row align-items-center justify-content-start"
                >
                  <span>{{ item.name || "" }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'status'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span :class="getTrainingStatusClass(props.row.status)">
                    {{ snakeToTitle(props.row.status) }}
                  </span>
                </div>
              </span>

              <span v-else class="d-flex align-items-center justify-content-start">
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import {
  formatCurrency,
  formatLongDate,
  formatTime,
  formatDate,
  modifyStatus,
  snakeToTitle,
  getTrainingStatusClass,
  formatTimeToHHMM,
} from "../utils/utils";

export default {
  name: "ExperientialLearningModal",

  components: {},

  props: {
    modalId: { type: String, default: "experiential-learning-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      memberColumns: [
        {
          label: "Role",
          field: "role",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "NIK",
          field: "nik",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Name",
          field: "name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Department",
          field: "department",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
      ],
      milestoneColumns: [
        {
          label: "Task",
          field: "task",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Start Date",
          field: "start_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "End Date",
          field: "end_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Actual Start Date",
          field: "actual_start_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Actual End Date",
          field: "actual_end_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Progress",
          field: "percentage",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Evidence",
          field: "evidence",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Feedback",
          field: "feedback",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Assigned",
          field: "assigned",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary align-items-center justify-content-start",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  computed: {},

  methods: {
    formatCurrency,
    snakeToTitle,
    getTrainingStatusClass,
    formatLongDate,
    formatTime,
    modifyStatus,
    formatDate,
    formatTimeToHHMM,
    badgeColorClass(type) {
      if (type === "M") return "badge-blue";
      return "badge-green";
    },

    previewPresentation() {
      if (this.data?.presentation_document) {
        const certUrl = `${this.data?.presentation_document}`;
        window.open(certUrl, "_blank");
      }
    },

    updateActivity(data) {
      this.$emit("open-update-activity-modal", data);
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}

.badge-custom {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.35rem;
}

.alert-heading {
  margin-bottom: 0.5rem;
}

:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}

.course-image-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: #fafafa;
}

.course-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

.pdf-thumbnail {
  cursor: pointer;
  padding: 2rem;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container {
  width: 80px;
  height: 100px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-label {
  font-weight: 600;
  color: #1a202c;
  font-size: 1rem;
}

.pdf-thumbnail-full {
  cursor: pointer;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail-full:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container-large {
  width: 100px;
  height: 130px;
  margin: 0 auto;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container-large i {
  font-size: 3.5rem;
  color: #fff;
}

/* Project details grid */
.project-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: start;
}

.project-item {
  min-width: 0;
}

.project-label {
  font-size: 0.85rem;
  color: #6b6b6b;
}

.project-value {
  font-weight: 600;
  color: #111827;
}

.desc-truncate {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
}

@media (max-width: 1200px) {
  .project-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .project-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .project-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>
